import type { ReactNode } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import {
  Box,
  Button,
  Checkbox,
  IconButton,
  MenuItem,
  Select,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';

import { GroupBlurb } from './HelpPanelDrawer';
import { IntakePill } from './IntakePill';
import {
  initialPayerPlanTypes,
  payerPlanLabel,
  payerPlanOptions,
  payerPrograms,
} from './payerPlans';
import { cardSx, footnoteSx, groupTitleSx, overlineSx, tableHeadSx, wf } from './tokens';
import type { PayerEnrollmentStatus, PayerPlanType, PayerRow } from './types';

export interface IntakePayerTableProps {
  canManage?: boolean;
  planSelectionEnabled?: boolean;
  derivedSentence?: string | null;
  blurb?: string | null;
  help?: ReactNode;
  loading?: boolean;
  /** Empty-state copy while the actor may edit the roster. */
  manageEmptyMessage?: string;
  /** Chip copy while the actor may edit the roster. */
  manageLabel?: string;
  /** Chip copy while the roster is read-only here (e.g. the aggregate org view). */
  readOnlyLabel?: string;
  onEnrollmentStatusChange?: (rowId: string, status: PayerEnrollmentStatus) => void;
  onPlanTypesChange?: (rowId: string, plans: PayerPlanType[]) => void;
  onRemove?: (rowId: string) => void;
  onServiceChange?: (rowId: string, service: 'jotcred' | 'jotbill', enabled: boolean) => void;
  rows: PayerRow[];
  search?: ReactNode;
  /** Save-state indicator shown beside the heading (providers autosave). */
  status?: ReactNode;
}

/**
 * Section copy from the "Payer List" design. jotcred is derived from the
 * section (off when already in-network, on when not in-network yet), so the
 * segmented control only toggles jotbill and always has one option selected.
 */
const SECTIONS: Record<
  PayerEnrollmentStatus,
  { title: string; blurb: string; emptyMessage: string; withoutBill: string; withBill: string }
> = {
  already_enrolled: {
    title: 'Already in-network',
    blurb: 'You already have a contract with these payers. We can file your claims.',
    emptyMessage: 'No payers are listed as already in-network.',
    withoutBill: 'No services',
    withBill: 'Billing',
  },
  wants_to_enroll: {
    title: 'Not in-network yet',
    blurb: "We'll get you contracted with these payers, and file your claims if you want.",
    emptyMessage: 'No payers are listed as not in-network yet.',
    withoutBill: 'Credentialing',
    withBill: 'Cred + billing',
  },
};

export function IntakePayerTable({
  canManage = false,
  planSelectionEnabled = false,
  derivedSentence,
  blurb,
  help,
  loading = false,
  manageEmptyMessage = 'No payers configured. Search above to add the first payer.',
  manageLabel,
  readOnlyLabel = 'read-only here',
  onEnrollmentStatusChange,
  onPlanTypesChange,
  onRemove,
  onServiceChange,
  rows,
  search,
  status,
}: IntakePayerTableProps) {
  const renderSection = (status: PayerEnrollmentStatus) => {
    const section = SECTIONS[status];
    const sectionRows = rows.filter(row => row.enrollmentStatus === status);
    const otherStatus: PayerEnrollmentStatus =
      status === 'already_enrolled' ? 'wants_to_enroll' : 'already_enrolled';
    const otherLabel = SECTIONS[otherStatus].title;

    return (
      <Box component="section" aria-labelledby={`payer-roster-${status}-heading`} sx={{ mb: 3 }}>
        <Typography
          component="h3"
          id={`payer-roster-${status}-heading`}
          sx={{ ...overlineSx, fontSize: '12px', letterSpacing: '0.11em', color: wf.slate }}
        >
          {section.title}
        </Typography>
        <Typography sx={{ color: wf.muted, fontSize: '13px', maxWidth: 620, mt: 0.5, mb: 1.5 }}>
          {section.blurb}
        </Typography>
        {sectionRows.length === 0 ? (
          <Typography sx={{ color: wf.muted, fontSize: '12.5px', py: 0.5 }}>
            {section.emptyMessage}
          </Typography>
        ) : (
          <Box
            sx={{
              border: `1px solid ${wf.line}`,
              borderRadius: '10px',
              overflowX: 'auto',
            }}
          >
            <Box
              component="table"
              aria-labelledby={`payer-roster-${status}-heading`}
              sx={{
                width: '100%',
                minWidth: 480,
                borderCollapse: 'collapse',
                fontSize: '14px',
                '& tbody tr:last-of-type td': { borderBottom: 0 },
              }}
            >
              <Box component="thead">
                <Box component="tr">
                  <Box
                    component="th"
                    scope="col"
                    sx={{ ...tableHeadSx, p: '10px 16px', textAlign: 'left' }}
                  >
                    Payer
                  </Box>
                  <Box
                    component="th"
                    scope="col"
                    sx={{
                      ...tableHeadSx,
                      p: '10px 16px',
                      textAlign: 'left',
                      whiteSpace: 'nowrap',
                      width: '1%',
                    }}
                  >
                    What we&apos;ll do
                  </Box>
                  {canManage && (
                    <Box component="th" aria-label="Actions" sx={{ ...tableHeadSx, width: 72 }} />
                  )}
                </Box>
              </Box>
              <Box component="tbody">
                {sectionRows.map(row => (
                  <Box component="tr" key={row.rowId}>
                    <Box
                      component="td"
                      sx={{ borderBottom: `1px solid ${wf.lineSoft}`, p: '12px 16px' }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          alignItems: 'baseline',
                          gap: '2px 10px',
                        }}
                      >
                        <Box component="span" sx={{ color: wf.ink, fontWeight: 500 }}>
                          {row.displayName}
                        </Box>
                        <Box
                          component="span"
                          sx={{
                            color: wf.meta,
                            fontSize: '12.5px',
                            fontVariantNumeric: 'tabular-nums',
                          }}
                        >
                          {row.payerId}
                        </Box>
                      </Box>
                      {planSelectionEnabled && (
                        <PayerPrograms
                          row={row}
                          canManage={canManage}
                          onChange={onPlanTypesChange}
                        />
                      )}
                    </Box>
                    <Box
                      component="td"
                      sx={{ borderBottom: `1px solid ${wf.lineSoft}`, p: '8px 16px' }}
                    >
                      <ToggleButtonGroup
                        aria-label={`${row.displayName} JotPsych services`}
                        color="primary"
                        disabled={!canManage}
                        exclusive
                        onChange={(_event, next: 'without_bill' | 'with_bill' | null) => {
                          if (next === null) return;
                          onServiceChange?.(row.rowId, 'jotbill', next === 'with_bill');
                        }}
                        size="small"
                        sx={{
                          '& .MuiToggleButton-root': {
                            borderColor: wf.slateLine,
                            color: wf.slate,
                            fontSize: '13px',
                            lineHeight: 1.2,
                            px: '14px',
                            py: '7px',
                            textTransform: 'none',
                            whiteSpace: 'nowrap',
                          },
                          '& .MuiToggleButton-root.Mui-selected, & .MuiToggleButton-root.Mui-selected:hover':
                            {
                              backgroundColor: wf.blueSoft,
                              color: wf.blue,
                              fontWeight: 600,
                            },
                        }}
                        value={row.jotbill ? 'with_bill' : 'without_bill'}
                      >
                        <ToggleButton value="without_bill">{section.withoutBill}</ToggleButton>
                        <ToggleButton value="with_bill">{section.withBill}</ToggleButton>
                      </ToggleButtonGroup>
                    </Box>
                    {canManage && (
                      <Box
                        component="td"
                        sx={{
                          borderBottom: `1px solid ${wf.lineSoft}`,
                          p: '0 8px 0 0',
                          textAlign: 'right',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        <Tooltip title={`Move to ${otherLabel}`}>
                          <IconButton
                            aria-label={`Move ${row.displayName} to ${otherLabel}`}
                            onClick={() => onEnrollmentStatusChange?.(row.rowId, otherStatus)}
                            size="small"
                            sx={{ color: wf.faint }}
                          >
                            <SwapVertIcon fontSize="inherit" />
                          </IconButton>
                        </Tooltip>
                        <IconButton
                          aria-label={`Remove ${row.displayName}`}
                          onClick={() => onRemove?.(row.rowId)}
                          size="small"
                          sx={{ color: wf.faint }}
                        >
                          <CloseIcon fontSize="inherit" />
                        </IconButton>
                      </Box>
                    )}
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Box sx={{ ...cardSx, gridColumn: '1 / -1', padding: { xs: '18px 20px', md: '22px 24px' } }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          flexWrap: 'wrap',
          mb: '10px',
        }}
      >
        <Box sx={groupTitleSx}>Your payer list</Box>
        {help}
        {(canManage ? manageLabel : readOnlyLabel) && (
          <IntakePill
            tone="slate"
            label={
              <Box
                component="span"
                sx={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}
              >
                {canManage ? manageLabel : readOnlyLabel}
                {canManage && (
                  <Box
                    component="span"
                    aria-hidden="true"
                    sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: wf.green }}
                  />
                )}
              </Box>
            }
          />
        )}
        {status}
        {rows.length > 0 && (
          <Typography sx={{ ml: 'auto', color: wf.meta, fontSize: '12px' }}>
            {rows.length} {rows.length === 1 ? 'payer' : 'payers'}
          </Typography>
        )}
      </Box>
      <GroupBlurb blurb={blurb} sx={{ mt: '-6px', mb: '10px' }} />

      {canManage && search && <Box sx={{ width: '100%', mb: 1.5 }}>{search}</Box>}

      {loading ? (
        <Typography role="status" sx={{ color: wf.muted, fontSize: '12.5px', py: 2 }}>
          Loading payer list…
        </Typography>
      ) : rows.length === 0 ? (
        <Typography sx={{ color: wf.muted, fontSize: '12.5px', py: 2 }}>
          {canManage
            ? manageEmptyMessage
            : 'No payers yet. Your payer list is where you tell us which payers to file with.'}
        </Typography>
      ) : (
        <>
          {renderSection('already_enrolled')}
          {renderSection('wants_to_enroll')}
        </>
      )}

      <Typography sx={{ ...footnoteSx, mt: 1.25 }}>
        Status is tracked separately for each payer and service.
      </Typography>

      {derivedSentence && (
        <Box
          sx={{
            mt: 1,
            fontSize: '11.5px',
            color: wf.amberWarn,
            bgcolor: wf.amberWarnSoft,
            borderRadius: '6px',
            p: '8px 10px',
          }}
        >
          {derivedSentence}
        </Box>
      )}
    </Box>
  );
}

function PayerPrograms({
  row,
  canManage,
  onChange,
}: {
  row: PayerRow;
  canManage: boolean;
  onChange: IntakePayerTableProps['onPlanTypesChange'];
}) {
  const programs = payerPrograms(row.programs);
  const options = payerPlanOptions(row.programs);
  const selected = row.planTypes?.length
    ? row.planTypes
    : (initialPayerPlanTypes(row.programs) ?? []);
  const isListed = (plan: PayerPlanType) => options.some(option => option.value === plan);
  const listedSelections = selected.filter(isListed);
  const unlistedSelections = selected.filter(plan => !isListed(plan));
  const unsupportedPrograms = programs.filter(
    program => !options.some(option => option.value === program),
  );
  const singleton = programs.length === 1 && options.length === 1;

  // No Stedi programs and nothing saved to review: the program line stays empty.
  if (programs.length === 0 && unlistedSelections.length === 0) return null;

  return (
    <Box sx={{ mt: '3px' }}>
      {singleton ? (
        <Typography sx={{ color: wf.muted, fontSize: '11.5px' }}>
          {unlistedSelections.length ? `Listed program: ${options[0].label}` : options[0].label}
        </Typography>
      ) : options.length > 0 && canManage ? (
        <>
          {listedSelections.length === 0 && (
            <Typography sx={{ color: wf.amberWarn, fontSize: '11px', mb: 0.5 }}>
              Which programs will your practice use?
            </Typography>
          )}
          <Select
            multiple
            displayEmpty
            size="small"
            inputProps={{ 'aria-label': `${row.displayName} programs` }}
            value={listedSelections}
            onChange={event => {
              const next = event.target.value;
              if (typeof next === 'string') return;
              // Other edits must not silently discard historical saved choices.
              onChange?.(row.rowId, [...next.filter(isListed), ...unlistedSelections]);
            }}
            renderValue={values =>
              values.length ? values.map(payerPlanLabel).join(', ') : 'Select programs'
            }
            sx={{
              width: '100%',
              fontSize: '11.5px',
              '& .MuiSelect-select': { py: 0.75, whiteSpace: 'normal' },
            }}
          >
            {options.map(option => (
              <MenuItem key={option.value} value={option.value}>
                <Checkbox
                  checked={listedSelections.includes(option.value)}
                  size="small"
                  aria-hidden="true"
                  tabIndex={-1}
                />
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </>
      ) : options.length > 0 ? (
        <Typography sx={{ color: wf.muted, fontSize: '11.5px' }}>
          {listedSelections.length
            ? listedSelections.map(payerPlanLabel).join(', ')
            : 'Programs not selected'}
        </Typography>
      ) : null}
      {unsupportedPrograms.length > 0 && (
        <Typography sx={{ color: wf.muted, fontSize: '11.5px', mt: 0.5 }}>
          Stedi lists {unsupportedPrograms.map(program => program.toUpperCase()).join(', ')}.
          Selection is not available for these programs yet.
        </Typography>
      )}
      {unlistedSelections.length > 0 && (
        <Box sx={{ mt: 0.5 }}>
          <Typography sx={{ color: wf.amberWarn, fontSize: '11.5px' }}>
            Review saved choices: {unlistedSelections.map(payerPlanLabel).join(', ')}. These are not
            listed by Stedi for this payer.
          </Typography>
          {canManage && (
            <Button
              size="small"
              onClick={() => onChange?.(row.rowId, listedSelections)}
              aria-label={`Remove unlisted saved programs for ${row.displayName}`}
              sx={{ fontSize: '11.5px', textTransform: 'none', p: 0 }}
            >
              Remove unlisted saved choices
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
}
